import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { buildAppMeta, SITE_URL } from "@/lib/seo";
import { useMemo, useState } from "react";
import { RequireAuth } from "@/components/RequireAuth";
import { AppShell } from "@/components/AppShell";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Skeleton } from "@/components/ui/skeleton";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  ChevronDown,
  LayoutGrid,
  Lock,
  Mail,
  MoreHorizontal,
  Plus,
  Rows3,
  Search,
  Star,
  Upload,
} from "lucide-react";
import { BulkAddClients } from "@/components/clients/BulkAddClients";
import { toast } from "sonner";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { SOURCE_OPTIONS, RATING_VALUES, fmtRating, STATUS_OPTIONS } from "@/lib/clients";
import { useTierGate } from "@/hooks/useSubscription";
import { UpgradeModal } from "@/components/billing/UpgradeModal";

type Client = {
  id: string;
  /** Starter-tier read-time visibility flag, computed in the database. */
  locked?: boolean;
  student_name: string;
  student_email: string | null;
  status: "invited" | "active" | "paused" | "inactive";
  skill_tags: string[] | null;
  notes: string | null;
  phone: string | null;
  coach_rating: number | null;
};

type SessionLite = { id: string; coach_student_id: string | null; session_date: string };
type PaymentLite = { session_id: string; amount_cents: number; paid_at: string | null };
type View = "table" | "cards";
type StatusFilter = "all" | Client["status"];
type SkillFilter = "all" | "lt3" | "3to4" | "4to5" | "gte5";
type RecencyFilter = "any" | "7d" | "30d" | "90d" | "stale";

export const Route = createFileRoute("/app/clients")({
  head: () => ({
    meta: buildAppMeta({
      title: "Clients — CoachQuick",
      description: "Manage your coaching clients, contact details, notes, and session history in one place.",
      path: "/app/clients",
    }),
    links: [{ rel: "canonical", href: `${SITE_URL}/app/clients` }],
  }),
  component: () => (
    <RequireAuth role="coach">
      <ClientsPage />
    </RequireAuth>
  ),
});

const AVATAR_PALETTE = [
  "bg-[#9fe870] text-[#054d28]",
  "bg-[#fdba74] text-[#7c2d12]",
  "bg-[#fcd34d] text-[#78350f]",
  "bg-[#7dd3fc] text-[#075985]",
  "bg-[#f0abfc] text-[#701a75]",
  "bg-[#a5b4fc] text-[#312e81]",
  "bg-[#fda4af] text-[#881337]",
];

function initials(name: string) {
  const parts = (name || "").trim().split(/\s+/).filter(Boolean);
  return ((parts[0]?.[0] ?? "") + (parts[1]?.[0] ?? "")).toUpperCase() || "?";
}

function paletteFor(seed: string) {
  let h = 0;
  for (let i = 0; i < seed.length; i++) h = (h * 31 + seed.charCodeAt(i)) >>> 0;
  return AVATAR_PALETTE[h % AVATAR_PALETTE.length];
}

function fmtMoney(cents: number) {
  return new Intl.NumberFormat(undefined, { style: "currency", currency: "USD" }).format(
    (cents ?? 0) / 100,
  );
}

function todayIso() {
  return new Date().toISOString().slice(0, 10);
}

function daysAgoShort(dateStr: string) {
  const d = new Date(dateStr);
  const diff = Math.floor((Date.now() - d.getTime()) / (1000 * 60 * 60 * 24));
  if (diff <= 0) return "Today";
  if (diff < 30) return `${diff}d ago`;
  if (diff < 365) return `${Math.floor(diff / 30)}mo ago`;
  return `${Math.floor(diff / 365)}y ago`;
}

function nextSessionLabel(dateStr: string) {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const [y, m, d] = dateStr.slice(0, 10).split("-").map(Number);
  if (!y || !m || !d) return dateStr;
  const target = new Date(y, m - 1, d);
  const diff = Math.round((target.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));
  if (diff === 0) return "Today";
  if (diff === 1) return "Tomorrow";
  if (diff > 1 && diff < 7)
    return target.toLocaleDateString(undefined, { weekday: "short" });
  return target.toLocaleDateString(undefined, { month: "short", day: "numeric" });
}

const STATUS_PILL: Record<Client["status"], string> = {
  active: "bg-[#e6f4ea] text-[#054d28] dark:bg-[#054d28]/30 dark:text-[#7ed492]",
  invited: "border border-border text-muted-foreground",
  paused: "bg-[#fff3c2] text-[#b86700] dark:bg-[#b86700]/20 dark:text-[#fcd34d]",
  inactive: "bg-muted text-muted-foreground",
};

const STATUS_LABEL: Record<Client["status"], string> = {
  active: "Active",
  invited: "Invited",
  paused: "Paused",
  inactive: "Inactive",
};

function ClientsPage() {
  const { user } = useAuth();
  const userId = user?.id;
  const qc = useQueryClient();
  const nav = useNavigate();
  const [open, setOpen] = useState(false);
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [rating, setRating] = useState<string>("");
  const [source, setSource] = useState<string>("");
  const [tagsInput, setTagsInput] = useState("");
  const [notes, setNotes] = useState("");
  const [busy, setBusy] = useState(false);
  const [importOpen, setImportOpen] = useState(false);
  const [showUpgrade, setShowUpgrade] = useState(false);
  const gate = useTierGate();

  const [view, setView] = useState<View>("table");
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState<StatusFilter>("all");
  const [skillFilter, setSkillFilter] = useState<SkillFilter>("all");
  const [recencyFilter, setRecencyFilter] = useState<RecencyFilter>("any");
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());

  // Read through the RPC so the database decides what's visible. On Starter,
  // clients outside the 5 most recent come back masked (first name + last
  // initial + locked flag) and no other field ever reaches the browser.
  const clientsQuery = useQuery({
    queryKey: ["clients", userId],
    enabled: !!userId,
    queryFn: async () => {
      const { data, error } = await supabase.rpc("list_my_clients" as never, {} as never);
      if (error) throw error;
      return ((data ?? []) as Array<Record<string, unknown>>).map((r) => ({
        id: r.id as string,
        locked: !!r.locked,
        student_name: (r.student_name as string) ?? "Client",
        student_email: (r.student_email as string | null) ?? null,
        status: ((r.status as Client["status"] | null) ?? "active") as Client["status"],
        skill_tags: (r.skill_tags as string[] | null) ?? null,
        notes: (r.notes as string | null) ?? null,
        phone: (r.phone as string | null) ?? null,
        coach_rating: r.coach_rating == null ? null : Number(r.coach_rating),
      })) as Client[];
    },
  });

  const sessionsAggQuery = useQuery({
    queryKey: ["clients-sessions-agg", userId],
    enabled: !!userId,
    queryFn: async () => {
      const { data } = await supabase
        .from("sessions")
        .select("id, coach_student_id, session_date")
        .eq("coach_id", userId!);
      return (data ?? []) as SessionLite[];
    },
  });

  const paymentsAggQuery = useQuery({
    queryKey: ["clients-payments-agg", userId],
    enabled: !!userId,
    queryFn: async () => {
      const { data } = await supabase
        .from("session_payments" as never)
        .select("session_id, amount_cents, paid_at")
        .eq("coach_id", userId!);
      return (data ?? []) as PaymentLite[];
    },
  });

  const clients = clientsQuery.data ?? [];
  const sessionsAgg = sessionsAggQuery.data ?? [];
  const paymentsAgg = paymentsAggQuery.data ?? [];

  const aggregates = useMemo(() => {
    const sessionsByClient = new Map<string, string[]>();
    const sessIdToClient = new Map<string, string>();
    for (const s of sessionsAgg) {
      if (!s.coach_student_id) continue;
      const arr = sessionsByClient.get(s.coach_student_id) ?? [];
      arr.push(s.session_date);
      sessionsByClient.set(s.coach_student_id, arr);
      sessIdToClient.set(s.id, s.coach_student_id);
    }

    const balanceByClient = new Map<string, number>();
    for (const p of paymentsAgg) {
      if (p.paid_at) continue;
      const cid = sessIdToClient.get(p.session_id);
      if (!cid) continue;
      balanceByClient.set(cid, (balanceByClient.get(cid) ?? 0) + (p.amount_cents ?? 0));
    }

    const today = todayIso();
    const result = new Map<
      string,
      {
        lessons: number;
        lastSeen: string | null;
        nextSession: string | null;
        balanceCents: number;
      }
    >();
    for (const [cid, dates] of sessionsByClient) {
      const past = dates.filter((d) => d <= today).sort().reverse();
      const future = dates.filter((d) => d > today).sort();
      result.set(cid, {
        lessons: past.length,
        lastSeen: past[0] ?? null,
        nextSession: future[0] ?? null,
        balanceCents: balanceByClient.get(cid) ?? 0,
      });
    }
    return result;
  }, [sessionsAgg, paymentsAgg]);

  const activeCount = useMemo(
    () => clients.filter((c) => !c.locked && c.status === "active").length,
    [clients],
  );
  const lockedCount = useMemo(() => clients.filter((c) => c.locked).length, [clients]);
  const visibleCount = clients.length - lockedCount;

  const filteredClients = useMemo(() => {
    const q = search.trim().toLowerCase();
    return clients.filter((c) => {
      // Locked clients have no readable fields, so any active field filter
      // excludes them; they still match a search on the masked name.
      if (c.locked) {
        if (statusFilter !== "all" || skillFilter !== "all" || recencyFilter !== "any") return false;
        if (q && !c.student_name.toLowerCase().includes(q)) return false;
        return true;
      }

      if (statusFilter !== "all" && c.status !== statusFilter) return false;

      if (skillFilter !== "all") {
        const r = c.coach_rating;
        if (r == null) return false;
        if (skillFilter === "lt3" && r >= 3) return false;
        if (skillFilter === "3to4" && (r < 3 || r >= 4)) return false;
        if (skillFilter === "4to5" && (r < 4 || r >= 5)) return false;
        if (skillFilter === "gte5" && r < 5) return false;
      }

      if (recencyFilter !== "any") {
        const last = aggregates.get(c.id)?.lastSeen;
        const days = last
          ? Math.floor((Date.now() - new Date(last).getTime()) / 86400000)
          : Number.POSITIVE_INFINITY;
        if (recencyFilter === "7d" && days > 7) return false;
        if (recencyFilter === "30d" && days > 30) return false;
        if (recencyFilter === "90d" && days > 90) return false;
        if (recencyFilter === "stale" && days <= 30) return false;
      }

      if (q) {
        const hay = `${c.student_name} ${c.student_email ?? ""} ${(c.skill_tags ?? []).join(" ")}`.toLowerCase();
        if (!hay.includes(q)) return false;
      }
      return true;
    });
  }, [clients, statusFilter, skillFilter, recencyFilter, search, aggregates]);

  function reset() {
    setName("");
    setEmail("");
    setPhone("");
    setRating("");
    setSource("");
    setTagsInput("");
    setNotes("");
  }

  async function addClient(e: React.FormEvent) {
    e.preventDefault();
    if (!user) return;
    setBusy(true);
    try {
      const { error } = await supabase.from("coach_students").insert({
        coach_id: user.id,
        student_name: name,
        student_email: email.toLowerCase().trim(),
        status: "active",
        phone: phone.trim() || null,
        coach_rating: rating ? Number(rating) : null,
        source: source ? (source as any) : null,
        skill_tags: tagsInput
          .split(",")
          .map((t) => t.trim())
          .filter(Boolean),
        notes: notes || null,
      } as never);
      if (error) throw error;

      toast.success("Client added");
      setOpen(false);
      reset();
      qc.invalidateQueries({ queryKey: ["clients", userId] });
      qc.invalidateQueries({ queryKey: ["coach-stats", userId] });
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Failed to add client");
    } finally {
      setBusy(false);
    }
  }

  const allSelected =
    filteredClients.length > 0 && filteredClients.every((c) => selectedIds.has(c.id));

  function toggleAll() {
    if (allSelected) setSelectedIds(new Set());
    else setSelectedIds(new Set(filteredClients.map((c) => c.id)));
  }

  function toggleOne(id: string, checked: boolean) {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (checked) next.add(id);
      else next.delete(id);
      return next;
    });
  }

  const statusFilterLabel =
    statusFilter === "all"
      ? "All"
      : STATUS_OPTIONS.find((s) => s.value === statusFilter)?.label ?? statusFilter;
  const skillFilterLabel =
    skillFilter === "all"
      ? "All"
      : skillFilter === "lt3"
        ? "<3.0"
        : skillFilter === "3to4"
          ? "3.0–3.9"
          : skillFilter === "4to5"
            ? "4.0–4.9"
            : "5.0+";
  const recencyFilterLabel =
    recencyFilter === "any"
      ? "Any"
      : recencyFilter === "7d"
        ? "Last 7d"
        : recencyFilter === "30d"
          ? "Last 30d"
          : recencyFilter === "90d"
            ? "Last 90d"
            : "30d+ stale";

  return (
    <AppShell>
      <div className="-mx-4 -my-6 min-h-[calc(100vh-4rem)] bg-canvas-soft px-4 py-6 md:-mx-8 md:px-8">
        {/* Header */}
        <div className="mb-6 flex flex-wrap items-end justify-between gap-4">
          <div>
            <p className="text-xs font-semibold uppercase tracking-[0.18em] text-muted-foreground">
              Roster · {activeCount} active
            </p>
            <h1 className="mt-2 font-display text-4xl font-black tracking-tight text-foreground md:text-5xl">
              Clients
            </h1>
            <p className="mt-2 text-sm text-muted-foreground">
              Everyone you coach. Filter by status, skill, or last session.
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Dialog open={importOpen} onOpenChange={setImportOpen}>
              <DialogTrigger asChild>
                <Button variant="outline" className="rounded-[10px]">
                  <Upload className="h-4 w-4" /> Import CSV
                </Button>
              </DialogTrigger>
              <DialogContent className="sm:max-w-2xl">
                <DialogHeader>
                  <DialogTitle>Import clients</DialogTitle>
                </DialogHeader>
                <BulkAddClients
                  onDone={() => {
                    setImportOpen(false);
                    qc.invalidateQueries({ queryKey: ["clients", userId] });
                    qc.invalidateQueries({ queryKey: ["coach-stats", userId] });
                  }}
                />
              </DialogContent>
            </Dialog>
            <Dialog
              open={open}
              onOpenChange={(o) => {
                setOpen(o);
                if (!o) reset();
              }}
            >
              <DialogTrigger asChild>
                <Button className="rounded-[10px]">
                  <Plus className="h-4 w-4" /> Add client
                </Button>
              </DialogTrigger>


              <DialogContent>
                <DialogHeader>
                  <DialogTitle>Add a new client</DialogTitle>
                </DialogHeader>
                <form onSubmit={addClient} className="space-y-4">
                  <div>
                    <Label htmlFor="cname">Full name</Label>
                    <Input
                      id="cname"
                      value={name}
                      onChange={(e) => setName(e.target.value)}
                      required
                      className="mt-1.5"
                    />
                  </div>
                  <div>
                    <Label htmlFor="cemail">Email</Label>
                    <Input
                      id="cemail"
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      required
                      className="mt-1.5"
                    />
                    <p className="mt-1 text-xs text-muted-foreground">
                      Used for emailing lesson recaps.
                    </p>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <Label htmlFor="cphone">Phone</Label>
                      <Input
                        id="cphone"
                        type="tel"
                        inputMode="tel"
                        placeholder="(555) 123-4567"
                        value={phone}
                        onChange={(e) => setPhone(e.target.value)}
                        className="mt-1.5"
                      />
                    </div>
                    <div>
                      <Label htmlFor="crating">Coach rating</Label>
                      <Select
                        value={rating || "__none__"}
                        onValueChange={(v) => setRating(v === "__none__" ? "" : v)}
                      >
                        <SelectTrigger className="mt-1.5">
                          <SelectValue placeholder="—" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="__none__">— Not set —</SelectItem>
                          {RATING_VALUES.map((r) => (
                            <SelectItem key={r} value={String(r)}>
                              {r.toFixed(1)}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  <div>
                    <Label htmlFor="csource">How did they find you?</Label>
                    <Select
                      value={source || "__none__"}
                      onValueChange={(v) => setSource(v === "__none__" ? "" : v)}
                    >
                      <SelectTrigger className="mt-1.5">
                        <SelectValue placeholder="—" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="__none__">— Not set —</SelectItem>
                        {SOURCE_OPTIONS.map((s) => (
                          <SelectItem key={s.value} value={s.value}>
                            {s.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label htmlFor="ctags">Skill tags (comma separated)</Label>
                    <Input
                      id="ctags"
                      placeholder="beginner, backhand, footwork"
                      value={tagsInput}
                      onChange={(e) => setTagsInput(e.target.value)}
                      className="mt-1.5"
                    />
                  </div>
                  <div>
                    <Label htmlFor="cnotes">Notes</Label>
                    <Textarea
                      id="cnotes"
                      rows={3}
                      value={notes}
                      onChange={(e) => setNotes(e.target.value)}
                      className="mt-1.5"
                    />
                  </div>
                  <Button type="submit" className="w-full" disabled={busy}>
                    {busy ? "Adding…" : "Add client"}
                  </Button>
                </form>
              </DialogContent>
            </Dialog>
          </div>
        </div>

        {/* Filter bar */}
        <div className="mb-4 flex flex-wrap items-center gap-2">
          <div className="relative flex-1 min-w-[220px] max-w-[360px]">
            <Search className="pointer-events-none absolute left-3 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Filter by name, email, tag…"
              className="h-9 rounded-[10px] border-transparent bg-transparent pl-8 text-sm focus-visible:border-border focus-visible:bg-card"
            />
          </div>

          <FilterChip
            label="Status"
            value={statusFilterLabel}
            active={statusFilter !== "all"}
          >
            <DropdownMenuLabel>Status</DropdownMenuLabel>
            <DropdownMenuRadioGroup
              value={statusFilter}
              onValueChange={(v) => setStatusFilter(v as StatusFilter)}
            >
              <DropdownMenuRadioItem value="all">All</DropdownMenuRadioItem>
              <DropdownMenuSeparator />
              {STATUS_OPTIONS.map((s) => (
                <DropdownMenuRadioItem key={s.value} value={s.value}>
                  {s.label}
                </DropdownMenuRadioItem>
              ))}
            </DropdownMenuRadioGroup>
          </FilterChip>

          <FilterChip label="Skill" value={skillFilterLabel} active={skillFilter !== "all"}>
            <DropdownMenuLabel>Skill (DUPR)</DropdownMenuLabel>
            <DropdownMenuRadioGroup
              value={skillFilter}
              onValueChange={(v) => setSkillFilter(v as SkillFilter)}
            >
              <DropdownMenuRadioItem value="all">All</DropdownMenuRadioItem>
              <DropdownMenuSeparator />
              <DropdownMenuRadioItem value="lt3">&lt; 3.0</DropdownMenuRadioItem>
              <DropdownMenuRadioItem value="3to4">3.0 – 3.9</DropdownMenuRadioItem>
              <DropdownMenuRadioItem value="4to5">4.0 – 4.9</DropdownMenuRadioItem>
              <DropdownMenuRadioItem value="gte5">5.0 +</DropdownMenuRadioItem>
            </DropdownMenuRadioGroup>
          </FilterChip>

          <FilterChip
            label="Last session"
            value={recencyFilterLabel}
            active={recencyFilter !== "any"}
          >
            <DropdownMenuLabel>Last session</DropdownMenuLabel>
            <DropdownMenuRadioGroup
              value={recencyFilter}
              onValueChange={(v) => setRecencyFilter(v as RecencyFilter)}
            >
              <DropdownMenuRadioItem value="any">Any</DropdownMenuRadioItem>
              <DropdownMenuSeparator />
              <DropdownMenuRadioItem value="7d">Last 7 days</DropdownMenuRadioItem>
              <DropdownMenuRadioItem value="30d">Last 30 days</DropdownMenuRadioItem>
              <DropdownMenuRadioItem value="90d">Last 90 days</DropdownMenuRadioItem>
              <DropdownMenuRadioItem value="stale">30 days+ stale</DropdownMenuRadioItem>
            </DropdownMenuRadioGroup>
          </FilterChip>

          <div className="ml-auto inline-flex items-center rounded-[10px] border bg-card p-0.5">
            <ViewBtn active={view === "cards"} onClick={() => setView("cards")}>
              <LayoutGrid className="h-3.5 w-3.5" /> Cards
            </ViewBtn>
            <ViewBtn active={view === "table"} onClick={() => setView("table")}>
              <Rows3 className="h-3.5 w-3.5" /> Table
            </ViewBtn>
          </div>
        </div>

        {lockedCount > 0 && (
          <button
            type="button"
            onClick={() => setShowUpgrade(true)}
            className="mb-4 flex w-full items-center gap-2 rounded-[12px] border border-dashed bg-card px-4 py-3 text-left text-sm text-muted-foreground hover:bg-canvas-soft/60"
          >
            <Lock className="h-3.5 w-3.5 shrink-0" />
            <span>
              Showing {visibleCount} of {clients.length} clients ·{" "}
              <span className="font-semibold text-foreground underline">Upgrade to see all</span>
            </span>
          </button>
        )}

        {/* Content */}
        {clientsQuery.isLoading ? (
          <div className="rounded-[16px] border bg-card p-4">
            <div className="space-y-2">
              {Array.from({ length: 6 }).map((_, i) => (
                <Skeleton key={i} className="h-12 w-full" />
              ))}
            </div>
          </div>
        ) : filteredClients.length === 0 ? (
          <div className="rounded-[16px] border bg-card p-12 text-center">
            <p className="text-sm text-muted-foreground">
              {clients.length === 0
                ? "No clients yet. Add your first one to get started."
                : "No clients match these filters."}
            </p>
          </div>
        ) : view === "table" ? (
          <div className="overflow-hidden rounded-[16px] border bg-card">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b text-[10px] font-semibold uppercase tracking-[0.14em] text-muted-foreground">
                  <th className="w-10 px-3 py-3 text-left">
                    <input
                      type="checkbox"
                      checked={allSelected}
                      onChange={toggleAll}
                      aria-label="Select all"
                      className="h-3.5 w-3.5 rounded border-border"
                    />
                  </th>
                  <th className="px-3 py-3 text-left font-semibold">Client</th>
                  <th className="px-3 py-3 text-left font-semibold">Status</th>
                  <th className="px-3 py-3 text-left font-semibold">Skill</th>
                  <th className="px-3 py-3 text-left font-semibold">Tags</th>
                  <th className="px-3 py-3 text-right font-semibold">Lessons</th>
                  <th className="px-3 py-3 text-left font-semibold">Last seen</th>
                  <th className="px-3 py-3 text-left font-semibold">Next</th>
                  <th className="px-3 py-3 text-right font-semibold">Balance</th>
                  <th className="w-10 px-3 py-3"></th>
                </tr>
              </thead>
              <tbody>
                {filteredClients.map((c) => {
                  const agg = aggregates.get(c.id);
                  const lessons = agg?.lessons ?? 0;
                  const balance = agg?.balanceCents ?? 0;
                  const goto = () =>
                    c.locked
                      ? setShowUpgrade(true)
                      : nav({ to: "/app/clients/$clientId", params: { clientId: c.id } });
                  return (
                    <tr
                      key={c.id}
                      onClick={goto}
                      className={`cursor-pointer border-b last:border-0 hover:bg-canvas-soft/60 dark:hover:bg-muted/40 ${
                        c.locked ? "text-muted-foreground" : ""
                      }`}
                    >
                      <td className="px-3 py-3" onClick={(e) => e.stopPropagation()}>
                        <input
                          type="checkbox"
                          checked={selectedIds.has(c.id)}
                          onChange={(e) => toggleOne(c.id, e.target.checked)}
                          aria-label="Select"
                          disabled={c.locked}
                          className="h-3.5 w-3.5 rounded border-border"
                        />
                      </td>
                      <td className="px-3 py-3">
                        <div className={`flex items-center gap-2.5 ${c.locked ? "opacity-70" : ""}`}>
                          <Avatar className="h-7 w-7 shrink-0">
                            <AvatarFallback
                              className={`text-[10px] font-bold ${
                                c.locked ? "bg-muted text-muted-foreground" : paletteFor(c.student_name)
                              }`}
                            >
                              {c.locked ? <Lock className="h-3 w-3" /> : initials(c.student_name)}
                            </AvatarFallback>
                          </Avatar>
                          <div className="min-w-0 leading-tight">
                            <p className="truncate font-medium">{c.student_name}</p>
                            <p className="truncate text-xs text-muted-foreground">
                              {c.locked ? "Locked on Starter" : c.student_email}
                            </p>
                          </div>
                        </div>
                      </td>
                      <td className="px-3 py-3">
                        {c.locked ? (
                          <span className="text-xs text-muted-foreground">—</span>
                        ) : (
                          <span
                            className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-[11px] font-semibold ${STATUS_PILL[c.status]}`}
                          >
                            {STATUS_LABEL[c.status]}
                          </span>
                        )}
                      </td>
                      {c.locked ? (
                        <>
                          <td className="px-3 py-3 text-xs text-muted-foreground">—</td>
                          <td className="px-3 py-3 text-xs text-muted-foreground">—</td>
                          <td className="px-3 py-3 text-right text-xs text-muted-foreground">—</td>
                          <td className="px-3 py-3 text-xs text-muted-foreground">—</td>
                          <td className="px-3 py-3 text-xs text-muted-foreground">—</td>
                          <td className="px-3 py-3 text-right text-xs text-muted-foreground">—</td>
                          <td className="px-3 py-3 text-right">
                            <Lock className="ml-auto h-3.5 w-3.5 text-muted-foreground" />
                          </td>
                        </>
                      ) : (
                        <>
                      <td className="px-3 py-3">
                        {c.coach_rating != null ? (
                          <span className="inline-flex items-center gap-1 text-xs font-semibold">
                            <Star className="h-3 w-3 fill-current text-[#9fe870]" />
                            {fmtRating(c.coach_rating)}
                          </span>
                        ) : (
                          <span className="text-xs text-muted-foreground">—</span>
                        )}
                      </td>
                      <td className="px-3 py-3">
                        <div className="flex flex-wrap gap-1">
                          {(c.skill_tags ?? []).slice(0, 2).map((t) => (
                            <span
                              key={t}
                              className="inline-flex items-center rounded-full bg-muted px-2 py-0.5 text-[10px] font-medium text-muted-foreground"
                            >
                              {t}
                            </span>
                          ))}
                          {(c.skill_tags?.length ?? 0) > 2 && (
                            <span className="text-[10px] text-muted-foreground">
                              +{(c.skill_tags?.length ?? 0) - 2}
                            </span>
                          )}
                          {(!c.skill_tags || c.skill_tags.length === 0) && (
                            <span className="text-xs text-muted-foreground">—</span>
                          )}
                        </div>
                      </td>
                      <td className="px-3 py-3 text-right font-semibold">{lessons || "0"}</td>
                      <td className="px-3 py-3 text-xs text-muted-foreground">
                        {agg?.lastSeen ? daysAgoShort(agg.lastSeen) : "—"}
                      </td>
                      <td className="px-3 py-3">
                        {agg?.nextSession ? (
                          <span className="inline-flex items-center rounded-full bg-[#e8eefb] px-2 py-0.5 text-[11px] font-semibold text-[#1d4ed8] dark:bg-[#1d4ed8]/25 dark:text-[#a5c6ff]">
                            {nextSessionLabel(agg.nextSession)}
                          </span>
                        ) : (
                          <span className="text-xs text-muted-foreground">—</span>
                        )}
                      </td>
                      <td
                        className={`px-3 py-3 text-right font-semibold ${
                          balance > 0
                            ? "text-[#b86700] dark:text-[#fcd34d]"
                            : "text-muted-foreground"
                        }`}
                      >
                        {balance > 0 ? fmtMoney(balance) : "$0"}
                      </td>
                      <td className="px-3 py-3" onClick={(e) => e.stopPropagation()}>
                        <RowMenu client={c} />
                      </td>
                        </>
                      )}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {filteredClients.map((c) => {
              const agg = aggregates.get(c.id);
              const balance = agg?.balanceCents ?? 0;
              if (c.locked) {
                return (
                  <Card
                    key={c.id}
                    onClick={() => setShowUpgrade(true)}
                    className="cursor-pointer rounded-[16px] border bg-card p-[18px] opacity-70 shadow-none transition hover:opacity-100"
                  >
                    <div className="flex items-start gap-3">
                      <Avatar className="h-10 w-10 shrink-0">
                        <AvatarFallback className="bg-muted text-muted-foreground">
                          <Lock className="h-4 w-4" />
                        </AvatarFallback>
                      </Avatar>
                      <div className="min-w-0 flex-1">
                        <p className="truncate font-semibold text-muted-foreground">
                          {c.student_name}
                        </p>
                        <p className="truncate text-xs text-muted-foreground">Locked on Starter</p>
                      </div>
                    </div>
                    <p className="mt-4 border-t pt-3 text-xs text-muted-foreground">
                      Upgrade to Pro to see notes, history, and contact info.
                    </p>
                  </Card>
                );
              }
              return (
                <Card
                  key={c.id}
                  className="rounded-[16px] border bg-card p-[18px] shadow-none transition hover:shadow-soft"
                >
                  <Link
                    to="/app/clients/$clientId"
                    params={{ clientId: c.id }}
                    className="block"
                  >
                    <div className="flex items-start gap-3">
                      <Avatar className="h-10 w-10 shrink-0">
                        <AvatarFallback
                          className={`text-xs font-bold ${paletteFor(c.student_name)}`}
                        >
                          {initials(c.student_name)}
                        </AvatarFallback>
                      </Avatar>
                      <div className="min-w-0 flex-1">
                        <p className="truncate font-semibold">{c.student_name}</p>
                        <p className="truncate text-xs text-muted-foreground">
                          {c.student_email}
                        </p>
                      </div>
                      <div onClick={(e) => e.preventDefault()}>
                        <RowMenu client={c} />
                      </div>
                    </div>

                    <div className="mt-3 flex flex-wrap items-center gap-1.5">
                      <span
                        className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-[11px] font-semibold ${STATUS_PILL[c.status]}`}
                      >
                        {STATUS_LABEL[c.status]}
                      </span>
                      {c.coach_rating != null && (
                        <span className="inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[11px] font-semibold">
                          <Star className="h-3 w-3 fill-current text-[#9fe870]" />
                          {fmtRating(c.coach_rating)}
                        </span>
                      )}
                    </div>

                    {c.skill_tags && c.skill_tags.length > 0 && (
                      <div className="mt-3 flex flex-wrap gap-1">
                        {c.skill_tags.slice(0, 4).map((t) => (
                          <span
                            key={t}
                            className="inline-flex items-center rounded-full bg-muted px-2 py-0.5 text-[10px] font-medium text-muted-foreground"
                          >
                            {t}
                          </span>
                        ))}
                      </div>
                    )}

                    <div className="mt-4 grid grid-cols-3 gap-2 border-t pt-3 text-center">
                      <div>
                        <p className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
                          Lessons
                        </p>
                        <p className="mt-0.5 font-display text-lg font-black tracking-tight">
                          {agg?.lessons ?? 0}
                        </p>
                      </div>
                      <div>
                        <p className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
                          Last seen
                        </p>
                        <p className="mt-0.5 text-xs font-medium">
                          {agg?.lastSeen ? daysAgoShort(agg.lastSeen) : "—"}
                        </p>
                      </div>
                      <div>
                        <p className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
                          Balance
                        </p>
                        <p
                          className={`mt-0.5 text-xs font-semibold ${
                            balance > 0
                              ? "text-[#b86700] dark:text-[#fcd34d]"
                              : "text-muted-foreground"
                          }`}
                        >
                          {balance > 0 ? fmtMoney(balance) : "$0"}
                        </p>
                      </div>
                    </div>
                  </Link>
                </Card>
              );
            })}
          </div>
        )}
      </div>
      <UpgradeModal
        open={showUpgrade}
        onOpenChange={setShowUpgrade}
        tier="pro"
        feature="Full client list"
        description={`Unlock your full client list — upgrade to Pro to see notes, history, and contact info for all ${clients.length} of your clients.`}
      />
    </AppShell>

  );
}

function FilterChip({
  label,
  value,
  active,
  children,
}: {
  label: string;
  value: string;
  active: boolean;
  children: React.ReactNode;
}) {
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          className={`inline-flex items-center gap-1.5 rounded-[10px] border bg-card px-3 py-1.5 text-xs font-semibold transition ${
            active
              ? "border-foreground/40 text-foreground"
              : "text-muted-foreground hover:text-foreground"
          }`}
        >
          {label}: <span className="text-foreground">{value}</span>
          <ChevronDown className="h-3 w-3" />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" className="w-48">
        {children}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

function ViewBtn({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`inline-flex items-center gap-1.5 rounded-[8px] px-2.5 py-1 text-xs font-semibold transition ${
        active ? "bg-foreground text-background" : "text-muted-foreground hover:text-foreground"
      }`}
    >
      {children}
    </button>
  );
}

function RowMenu({ client }: { client: Client }) {
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          aria-label="Client actions"
          className="flex h-7 w-7 items-center justify-center rounded-[8px] border text-muted-foreground hover:text-foreground"
        >
          <MoreHorizontal className="h-3.5 w-3.5" />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        <DropdownMenuItem asChild>
          <Link to="/app/clients/$clientId" params={{ clientId: client.id }}>
            View profile
          </Link>
        </DropdownMenuItem>
        <DropdownMenuItem asChild>
          <Link to="/app/log" search={{ clientId: client.id } as never}>
            Log session
          </Link>
        </DropdownMenuItem>
        {client.student_email && (
          <DropdownMenuItem
            onSelect={() => {
              window.location.href = `mailto:${client.student_email}`;
            }}
          >
            <Mail className="h-3.5 w-3.5" /> Email
          </DropdownMenuItem>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
